import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import {
  notesApi,
  type NoteCreate,
  type NoteOut,
  type NotePatch,
  type NoteTargetKind,
  type NotesByTargetResponse,
  type NotesCountsResponse,
  type NotesListParams,
  type NotesRecentResponse,
} from "../api/notes";

export type NoteTargetRef = { kind: NoteTargetKind; id: string };

// Query keys grouped under a stable root so invalidations can wildcard
// the whole notes namespace if needed.
export const notesQK = {
  all: ["notes"] as const,
  recent: (limit: number) => ["notes", "recent", limit] as const,
  counts: (targets: ReadonlyArray<NoteTargetRef>) =>
    ["notes", "counts", targets.map((t) => `${t.kind}:${t.id}`).sort()] as const,
  byTarget: (kind: NoteTargetKind, id: string) =>
    ["notes", "byTarget", kind, id] as const,
  list: (params: NotesListParams) => ["notes", "list", params] as const,
};

function sortTargets(targets: ReadonlyArray<NoteTargetRef>): NoteTargetRef[] {
  return [...targets].sort((a, b) =>
    `${a.kind}:${a.id}`.localeCompare(`${b.kind}:${b.id}`),
  );
}

export function useRecentNotes({ limit }: { limit: number }) {
  return useQuery({
    queryKey: notesQK.recent(limit),
    queryFn: () => notesApi.recent({ limit }),
    refetchInterval: 30_000,  // dashboard ambient refresh (spec §5)
  });
}

export function useNotesCounts(targets: Array<NoteTargetRef>) {
  // F1: the queryKey is computed from the sorted serialised form to keep
  // semantically-equivalent target sets sharing a single cache entry.
  // The queryFn MUST close over the same sorted form so the URL we issue
  // never drifts from the key under which the result is cached.
  const sortedTargets = useMemo(() => sortTargets(targets), [targets]);
  return useQuery({
    queryKey: notesQK.counts(sortedTargets),
    queryFn: () => notesApi.counts(sortedTargets),
    enabled: sortedTargets.length > 0,
  });
}

export function useNotesByTarget({
  targetKind,
  targetId,
}: {
  targetKind: NoteTargetKind;
  targetId: string;
}) {
  return useQuery({
    queryKey: notesQK.byTarget(targetKind, targetId),
    queryFn: () => notesApi.byTarget({ targetKind, targetId }),
    enabled: targetId.length > 0,
  });
}

export function useListNotes(params: NotesListParams) {
  return useQuery({
    queryKey: notesQK.list(params),
    queryFn: () => notesApi.list(params),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations with optimistic updates (F2)
//
// Pattern: onMutate snapshots affected queries, applies the optimistic change,
// returns a context bag of [QueryKey, previousData] pairs. onError rolls back
// from the bag. onSettled invalidates the same keys so eventual reconciliation
// matches server truth.
// ─────────────────────────────────────────────────────────────────────────────

interface OptimisticContext {
  snapshots: Array<[QueryKey, unknown]>;
}

function snapshotAndSet<T>(
  qc: ReturnType<typeof useQueryClient>,
  key: QueryKey,
  updater: (prev: T | undefined) => T | undefined,
  ctx: OptimisticContext,
) {
  const prev = qc.getQueryData<T>(key);
  ctx.snapshots.push([key, prev]);
  qc.setQueryData<T>(key, updater(prev));
}

function rollback(qc: ReturnType<typeof useQueryClient>, ctx: OptimisticContext) {
  for (const [key, data] of ctx.snapshots) {
    qc.setQueryData(key, data);
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

// Build a synthetic optimistic NoteOut. user_id is unknown at this stage; we
// leave it empty — UI components that gate on currentUserId still render the
// row, just without owner-only affordances until the server replaces the stub.
function buildOptimisticNote(body: NoteCreate, tempId: string): NoteOut {
  const ts = nowIso();
  return {
    id: tempId,
    user_id: "",
    target_kind: body.target_kind ?? null,
    target_id: body.target_id ?? null,
    body: body.body,
    created_at: ts,
    updated_at: ts,
    archived_at: null,
  };
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NoteCreate) => notesApi.create(body),
    onMutate: async (body): Promise<OptimisticContext> => {
      // Cancel in-flight notes queries so they don't overwrite optimistic state
      await qc.cancelQueries({ queryKey: notesQK.all });

      const ctx: OptimisticContext = { snapshots: [] };
      const tempId = `tmp-${Date.now()}`;
      const optimistic = buildOptimisticNote(body, tempId);

      // Patch every cached recent list
      const recentMatches = qc.getQueriesData<NotesRecentResponse>({
        queryKey: ["notes", "recent"],
      });
      for (const [key, prev] of recentMatches) {
        ctx.snapshots.push([key, prev]);
        qc.setQueryData<NotesRecentResponse>(key, {
          items: [optimistic, ...(prev?.items ?? [])],
        });
      }

      // Patch the per-target list if this note has a target
      if (body.target_kind && body.target_id) {
        const key = notesQK.byTarget(body.target_kind, body.target_id);
        snapshotAndSet<NotesByTargetResponse>(
          qc,
          key,
          (prev) => ({ items: [optimistic, ...(prev?.items ?? [])] }),
          ctx,
        );

        // Bump every cached counts query that covers this target
        const targetSerial = `${body.target_kind}:${body.target_id}`;
        const countsMatches = qc.getQueriesData<NotesCountsResponse>({
          queryKey: ["notes", "counts"],
        });
        for (const [countsKey, prev] of countsMatches) {
          // The serialised target list is the third element of the key tuple.
          const serials = countsKey[2] as ReadonlyArray<string> | undefined;
          if (!Array.isArray(serials) || !serials.includes(targetSerial)) {
            continue;
          }
          ctx.snapshots.push([countsKey, prev]);
          qc.setQueryData<NotesCountsResponse>(countsKey, {
            ...(prev ?? {}),
            [targetSerial]: (prev?.[targetSerial] ?? 0) + 1,
          });
        }
      }

      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) rollback(qc, ctx);
    },
    onSettled: (_data, _err, vars) => {
      // Narrower invalidation (bonus): recent + per-target + counts.
      void qc.invalidateQueries({ queryKey: ["notes", "recent"] });
      void qc.invalidateQueries({ queryKey: ["notes", "list"] });
      if (vars.target_kind && vars.target_id) {
        void qc.invalidateQueries({
          queryKey: notesQK.byTarget(vars.target_kind, vars.target_id),
        });
      }
      void qc.invalidateQueries({ queryKey: ["notes", "counts"] });
    },
  });
}

export function usePatchNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: NotePatch }) =>
      notesApi.patch(id, patch),
    onMutate: async ({ id, patch }): Promise<OptimisticContext> => {
      await qc.cancelQueries({ queryKey: notesQK.all });

      const ctx: OptimisticContext = { snapshots: [] };

      const patchNote = (n: NoteOut): NoteOut => {
        let next = n;
        if (typeof patch.body === "string") {
          next = { ...next, body: patch.body, updated_at: nowIso() };
        }
        if (typeof patch.archived === "boolean") {
          next = {
            ...next,
            archived_at: patch.archived ? nowIso() : null,
            updated_at: nowIso(),
          };
        }
        return next;
      };

      // Patch every cached recent list
      const recentMatches = qc.getQueriesData<NotesRecentResponse>({
        queryKey: ["notes", "recent"],
      });
      for (const [key, prev] of recentMatches) {
        if (!prev) continue;
        ctx.snapshots.push([key, prev]);
        qc.setQueryData<NotesRecentResponse>(key, {
          items: prev.items.map((n) => (n.id === id ? patchNote(n) : n)),
        });
      }

      // Patch every cached by-target list
      const byTargetMatches = qc.getQueriesData<NotesByTargetResponse>({
        queryKey: ["notes", "byTarget"],
      });
      for (const [key, prev] of byTargetMatches) {
        if (!prev) continue;
        ctx.snapshots.push([key, prev]);
        qc.setQueryData<NotesByTargetResponse>(key, {
          items: prev.items.map((n) => (n.id === id ? patchNote(n) : n)),
        });
      }

      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) rollback(qc, ctx);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notesQK.all });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notesApi.remove(id),
    onMutate: async (id): Promise<OptimisticContext> => {
      await qc.cancelQueries({ queryKey: notesQK.all });

      const ctx: OptimisticContext = { snapshots: [] };

      // Locate the note so we know which target's count to decrement
      let removedTarget: { kind: NoteTargetKind; id: string } | null = null;

      // Remove from every cached recent list
      const recentMatches = qc.getQueriesData<NotesRecentResponse>({
        queryKey: ["notes", "recent"],
      });
      for (const [key, prev] of recentMatches) {
        if (!prev) continue;
        const hit = prev.items.find((n) => n.id === id);
        if (hit?.target_kind && hit.target_id && !removedTarget) {
          removedTarget = { kind: hit.target_kind, id: hit.target_id };
        }
        ctx.snapshots.push([key, prev]);
        qc.setQueryData<NotesRecentResponse>(key, {
          items: prev.items.filter((n) => n.id !== id),
        });
      }

      // Remove from every cached by-target list
      const byTargetMatches = qc.getQueriesData<NotesByTargetResponse>({
        queryKey: ["notes", "byTarget"],
      });
      for (const [key, prev] of byTargetMatches) {
        if (!prev) continue;
        const hit = prev.items.find((n) => n.id === id);
        if (hit?.target_kind && hit.target_id && !removedTarget) {
          removedTarget = { kind: hit.target_kind, id: hit.target_id };
        }
        ctx.snapshots.push([key, prev]);
        qc.setQueryData<NotesByTargetResponse>(key, {
          items: prev.items.filter((n) => n.id !== id),
        });
      }

      // Decrement counts for the affected target
      if (removedTarget) {
        const target = removedTarget;
        const targetSerial = `${target.kind}:${target.id}`;
        const countsMatches = qc.getQueriesData<NotesCountsResponse>({
          queryKey: ["notes", "counts"],
        });
        for (const [countsKey, prev] of countsMatches) {
          const serials = countsKey[2] as ReadonlyArray<string> | undefined;
          if (!Array.isArray(serials) || !serials.includes(targetSerial)) {
            continue;
          }
          if (!prev || !(targetSerial in prev)) continue;
          ctx.snapshots.push([countsKey, prev]);
          qc.setQueryData<NotesCountsResponse>(countsKey, {
            ...prev,
            [targetSerial]: Math.max(0, (prev[targetSerial] ?? 0) - 1),
          });
        }
      }

      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) rollback(qc, ctx);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notesQK.all });
    },
  });
}
